import React, { useEffect, useMemo, useState } from 'react';
import { Pagination } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, DrawerModal, EmptyState } from '../../../../components/common';
import { formatNumberString } from '../../../../utility/utility';
import {
  adLicenseListModalOpenEvent,
  adLicenseFilterAppliedEvent,
  adLicenseClickEvent,
} from '../../../../services/analyticsService';
import { PAGE_SECTIONS, INTERACTED_FROM } from '../../../../services/analyticsService/constants';
import { useGetRegaAdLicensesQuery } from '../../apis/regaAdLicenses';
import { isIndividualSeller as checkIndividualSeller } from './utils';
import { SkeletonBlock, AdLicenseSheetStyles } from './styled';
import Icon from '../../../../components/common/icon/icon';
import { CloseIcon, ChevronLeftIcon } from './icons';
import AdLicenseListRow from './ad-license-list-row';
import AdLicenseListFilters from './ad-license-list-filters';
import AssistanceStatus from './assistance-status';
import RequestAssistanceContent from './request-assistance-content';
import RegaAdLicenseDetail from './rega-ad-license-detail';
import {
  SkeletonGrid,
  StatePad,
  ListGrid,
  AssistPanel,
  AssistHeaderDesktop,
  AssistHeaderLeft,
  BackButton,
  HeaderDivider,
  AssistHeaderTitle,
  CloseButton,
  AssistBody,
  ListPanel,
  ListHeader,
  ListTitleGroup,
  ListTitle,
  ChipDivider,
  MobileActionRow,
  FilterIconButton,
  DesktopFiltersBar,
  ListScroll,
  ListFooter,
  ShowingText,
  AssistSheet,
  SheetHeader,
  SheetTitle,
  SheetBody,
  FilterSheet,
  FilterBody,
  FilterFooter,
} from './ad-licenses-list-modal.styled';
import { v2Color } from '@theme/v2';

const PER_PAGE = 10;
const DEFAULT_QUERY = { 'q[s]': 'rega_ad_license_issue_date desc' };

const getUserRole = (loginUser) => {
  if (loginUser?.is_agency_admin) return 'owner';
  if (checkIndividualSeller(loginUser)) return 'individual_seller';
  return 'agent';
};

const AdLicensesListModal = ({ visible, onClose, onPostLicense, summary }) => {
  const { t } = useTranslation();
  const { user: loginUser } = useSelector((state) => state.app.loginUser);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const isIndividual = checkIndividualSeller(loginUser);
  const sidePad = isMobile ? 16 : 24;

  const [view, setView] = useState('list');
  const [detailId, setDetailId] = useState(null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(DEFAULT_QUERY);
  // Mobile only: filters live in a second bottom sheet opened from a filter icon in the header.
  const [filterOpen, setFilterOpen] = useState(false);
  // Bumped on reset to remount the filters so the uncontrolled search input clears too.
  const [filtersKey, setFiltersKey] = useState(0);
  // Mobile sheet stages its edits here and only commits them to `query` on "Search" (the
  // desktop inline filters still apply live via handleFilterChange).
  const [draftQuery, setDraftQuery] = useState(DEFAULT_QUERY);

  const {
    data,
    isFetching,
    isError,
    refetch,
  } = useGetRegaAdLicensesQuery(
    { page, per_page: PER_PAGE, ...query },
    { skip: !visible, refetchOnMountOrArgChange: true },
  );

  const list = data?.list || [];
  const totalCount = data?.pagination?.total_count ?? 0;
  const unpostedCount = summary?.unposted_count ?? totalCount;

  useEffect(() => {
    if (visible) {
      setView('list');
      adLicenseListModalOpenEvent(loginUser, { value: summary?.unposted_count, userRole: getUserRole(loginUser) });
    } else {
      // Clear filters/search/pagination so reopening the modal starts fresh.
      setQuery(DEFAULT_QUERY);
      setPage(1);
      setView('list');
      setDetailId(null);
      setFilterOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleFilterChange = (patch) => {
    setQuery((prev) => ({ ...prev, ...patch }));
    setPage(1);
    adLicenseFilterAppliedEvent(loginUser, Object.keys(patch)[0]);
  };

  // --- Mobile filter sheet (staged) ---
  const openFilters = () => {
    setDraftQuery(query); // start from whatever is currently applied
    setFilterOpen(true);
  };
  const handleDraftChange = (patch) => setDraftQuery((prev) => ({ ...prev, ...patch }));
  // Reset behaves like My Listings: clear the filters, refetch the list without any filters, and
  // close the sheet.
  const resetDraft = () => {
    setQuery(DEFAULT_QUERY);
    setDraftQuery(DEFAULT_QUERY);
    setPage(1);
    setFiltersKey((prev) => prev + 1); // remount so the uncontrolled search input clears
    setFilterOpen(false);
  };
  const applyFilters = () => {
    setQuery(draftQuery);
    setPage(1);
    setFilterOpen(false);
    Object.keys(draftQuery)
      .filter((key) => key !== 'q[s]' && draftQuery[key] !== DEFAULT_QUERY[key])
      .forEach((key) => adLicenseFilterAppliedEvent(loginUser, key));
  };

  const goAssist = () => {
    adLicenseClickEvent(loginUser, {
      pageSection: PAGE_SECTIONS.ASSISTANCE,
      interactedFrom: INTERACTED_FROM.LIST_MODAL,
    });
    setView('assist');
  };

  const openDetail = (row) => {
    adLicenseClickEvent(loginUser, { pageSection: PAGE_SECTIONS.AD_LICENSE_LIST, value: row?.ad_license_number });
    setDetailId(row?.id);
    setView('detail');
  };

  const postNow = (row) => {
    onClose();
    onPostLicense(row?.ad_license_number, PAGE_SECTIONS.AD_LICENSE_LIST);
  };

  const rangeLabel = useMemo(() => {
    if (!totalCount) return '0';
    const start = (page - 1) * PER_PAGE + 1;
    const end = Math.min(page * PER_PAGE, totalCount);
    return `${start}–${end}`;
  }, [page, totalCount]);

  const listBody = () => {
    if (isFetching) {
      return (
        <SkeletonGrid>
          {Array.from({ length: 6 }).map((_, idx) => (
            <SkeletonBlock key={idx} height="72px" radius="8px" />
          ))}
        </SkeletonGrid>
      );
    }
    if (isError) {
      return (
        <StatePad>
          <EmptyState type="analytics-dashboard" message={t('Something went wrong')} onClick={refetch} />
        </StatePad>
      );
    }
    if (!list.length) {
      return (
        <StatePad>
          <EmptyState type="table" hideRetryButton />
        </StatePad>
      );
    }
    return (
      <ListGrid $isMobile={isMobile} $sidePad={sidePad}>
        {list.map((row) => (
          <AdLicenseListRow
            key={row.id}
            row={row}
            isIndividualSeller={isIndividual}
            isMobile={isMobile}
            onViewDetails={openDetail}
            onPostNow={postNow}
          />
        ))}
      </ListGrid>
    );
  };

  return (
    <>
      {isMobile && <AdLicenseSheetStyles />}
      <DrawerModal
        visible={visible}
        onCancel={onClose}
        width="min(1120px, calc(100vw - 48px))"
        // Desktop: keep the mask at AntD's default z-index (below the side rail at 1002) so the rail
        // is dimmed once by the layout's rail scrim (scrim z = maskZ + 1), uniform with the content
        // dim; the dialog wrapper is lifted to 1300 to render in front of the side nav. Mobile has no
        // rail but the post-listing page chrome (e.g. the Back button) sits above the default mask, so
        // raise the whole sheet to 1200 there to cover it.
        zIndex={isMobile ? 1200 : undefined}
        styles={{ mask: { background: 'rgba(0, 0, 0, 0.45)' }, wrapper: { zIndex: 1300 } }}
        title={null}
        closable={false}
        // On mobile the shared Drawer renders its own close icon in the header `extra` slot; our
        // content already renders a header + close, so suppress the built-in one to avoid two
        // crosses. (Desktop uses AntD Modal, which ignores `extra`.)
        extra={isMobile ? null : undefined}
        footer={null}
        destroyOnClose
        className="ad-license-sheet ad-license-list-modal"
      >
        {view === 'detail' && !isMobile ? (
          <RegaAdLicenseDetail
            id={detailId}
            // hideBack drops the header Back only; the footer "Back to list" (next to Post Now)
            // stays and returns to the list, matching the existing "Listing Details from REGA" modal.
            onBack={() => setView('list')}
            onClose={() => setView('list')}
            onPostLicense={(number) => {
              onClose();
              onPostLicense(number, PAGE_SECTIONS.AD_LICENSE_LIST);
            }}
            hideBack
          />
        ) : view === 'assist' && !isMobile ? (
          <AssistPanel $isMobile={isMobile}>
            <AssistHeaderDesktop $isMobile={isMobile} $sidePad={sidePad}>
              <AssistHeaderLeft>
                <BackButton type="button" onClick={() => setView('list')}>
                  <ChevronLeftIcon size={16} />
                  <span>{t('Back')}</span>
                </BackButton>
                <HeaderDivider />
                <AssistHeaderTitle>{t('Request Assistance')}</AssistHeaderTitle>
              </AssistHeaderLeft>
              <CloseButton type="button" onClick={onClose} aria-label={t('Close')}>
                <CloseIcon size={16} />
              </CloseButton>
            </AssistHeaderDesktop>
            {/* Full-width, tall panel matching the HTML prototype: the message is vertically
                centred and the footer (divider + buttons) pins to the bottom. RequestAssistanceContent
                fills this height, so the message centres and the buttons sit bottom-right. */}
            <AssistBody $isMobile={isMobile}>
              <RequestAssistanceContent
                unpostedCount={summary?.unposted_count}
                onCancel={() => setView('list')}
                onSent={() => setView('list')}
                bodyPadding="24px 24px 32px"
              />
            </AssistBody>
          </AssistPanel>
        ) : (
          // Use a fixed height (not maxHeight) for the list view: the body grows from the short
          // skeleton to ~10 rows once data loads, which — on a centred modal — makes it expand and
          // re-centre (the "jerk"). A stable height keeps the loading and loaded states the same
          // size; the list area scrolls internally.
          <ListPanel $isMobile={isMobile}>
            {/* Centre the close (X) with the title. On mobile the chip now sits in its own row
                below, so the header is a single line and the X aligns to the heading's centre.
                Caps sit high in the line box, so symmetric padding reads top-heavy; a slightly
                smaller top padding makes the title text look vertically centred. */}
            <ListHeader $isMobile={isMobile} $sidePad={sidePad}>
              <ListTitleGroup $isMobile={isMobile}>
                <ListTitle $isMobile={isMobile}>
                  {t('Active Ad Licenses to Post ({{n}})', { n: formatNumberString(unpostedCount) })}
                </ListTitle>
                {/* Desktop keeps the assistance chip inline next to the title; on mobile it moves to
                    a sub-row below the header (see below). */}
                {!isMobile && (
                  <ChipDivider>
                    <AssistanceStatus assistance={summary?.assistance} onReachOut={goAssist} showChip />
                  </ChipDivider>
                )}
              </ListTitleGroup>
              <CloseButton type="button" onClick={onClose} aria-label={t('Close')}>
                <CloseIcon size={16} />
              </CloseButton>
            </ListHeader>

            {/* Mobile: assistance chip (left) + filter icon (right) sit in a row below the header,
                not in the header. The filter icon matches the My Listings page (MdFilterList) and
                opens the filter bottom sheet. */}
            {isMobile && (
              // Same grey as the list area so the chip + filter sit on a continuous "empty area"
              // (like the My Listings page) rather than a white band with cards showing behind.
              <MobileActionRow $sidePad={sidePad}>
                <AssistanceStatus assistance={summary?.assistance} onReachOut={goAssist} showChip />
                {/* Always keep the filter icon on the right, even when there's no chip (CTA/none). */}
                <FilterIconButton type="button" onClick={openFilters} aria-label={t('Filters')}>
                  <Icon icon="MdFilterList" color={v2Color('#006169')} size={20} />
                </FilterIconButton>
              </MobileActionRow>
            )}

            {/* Desktop: filters inline. Mobile: moved into a dedicated filter bottom sheet opened
                from the filter icon above. */}
            {!isMobile && (
              <DesktopFiltersBar $sidePad={sidePad}>
                <AdLicenseListFilters query={query} onChange={handleFilterChange} isMobile={isMobile} />
              </DesktopFiltersBar>
            )}

            <ListScroll>{listBody()}</ListScroll>

            {/* More breathing room between the "Showing…" text and the pagination on mobile. */}
            <ListFooter $isMobile={isMobile} $sidePad={sidePad}>
              <ShowingText $isMobile={isMobile}>
                {t('Showing {{range}} of {{n}} Active Ad Licenses not posted', {
                  range: rangeLabel,
                  n: formatNumberString(totalCount),
                })}
              </ShowingText>
              {/* Match the My Listings pagination exactly: default (boxed) size on every
                  breakpoint, same className — no "small" variant on mobile. */}
              <Pagination
                className="pagination"
                current={page}
                total={totalCount}
                pageSize={PER_PAGE}
                showSizeChanger={false}
                // Keep arrows + page numbers on a single row (no wrapping onto a second line).
                style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}
                onChange={(nextPage) => setPage(nextPage)}
              />
            </ListFooter>
          </ListPanel>
        )}
      </DrawerModal>

      {/* Mobile-only detail bottom sheet. Stacks on top of the list sheet; the cross returns to the
          list (no Back button). Desktop renders the detail in-place inside the modal instead. */}
      {isMobile && (
        <DrawerModal
          visible={view === 'detail'}
          onCancel={() => setView('list')}
          title={null}
          closable={false}
          extra={null}
          footer={null}
          destroyOnClose
          zIndex={1400}
          className="ad-license-sheet ad-license-detail-sheet"
        >
          <RegaAdLicenseDetail
            id={detailId}
            onBack={() => setView('list')}
            onClose={() => setView('list')}
            onPostLicense={(number) => {
              onClose();
              onPostLicense(number, PAGE_SECTIONS.AD_LICENSE_LIST);
            }}
            hideBack
          />
        </DrawerModal>
      )}

      {/* Mobile-only Request Assistance bottom sheet. Stacks on the list sheet; the cross returns to
          the list (no Back button). Desktop renders the assist view in-place instead. */}
      {isMobile && (
        <DrawerModal
          visible={view === 'assist'}
          onCancel={() => setView('list')}
          title={null}
          closable={false}
          extra={null}
          footer={null}
          destroyOnClose
          zIndex={1400}
          className="ad-license-sheet ad-license-assist-sheet"
        >
          <AssistSheet>
            <SheetHeader $sidePad={sidePad}>
              <SheetTitle>{t('Request Assistance')}</SheetTitle>
              <CloseButton type="button" onClick={() => setView('list')} aria-label={t('Close')}>
                <CloseIcon size={16} />
              </CloseButton>
            </SheetHeader>
            <SheetBody>
              <RequestAssistanceContent
                unpostedCount={summary?.unposted_count}
                onCancel={() => setView('list')}
                onSent={() => setView('list')}
                bodyPadding="24px 24px 32px"
              />
            </SheetBody>
          </AssistSheet>
        </DrawerModal>
      )}

      {/* Mobile-only filter bottom sheet. Opens on top of the list sheet (higher z-index) from the
          header filter icon; closing it returns to the list sheet underneath. Filters apply live to
          the list via handleFilterChange. */}
      {isMobile && (
        <DrawerModal
          visible={filterOpen}
          onCancel={() => setFilterOpen(false)}
          title={null}
          closable={false}
          extra={null}
          footer={null}
          destroyOnClose
          zIndex={1400}
          className="ad-license-sheet ad-license-filter-sheet"
        >
          <FilterSheet>
            <SheetHeader $sidePad={sidePad}>
              <SheetTitle>{t('Filters')}</SheetTitle>
              <CloseButton type="button" onClick={() => setFilterOpen(false)} aria-label={t('Close')}>
                <CloseIcon size={16} />
              </CloseButton>
            </SheetHeader>
            <FilterBody $sidePad={sidePad}>
              <AdLicenseListFilters key={filtersKey} query={draftQuery} onChange={handleDraftChange} isMobile />
            </FilterBody>
            {/* Two-button footer identical to My Listings' filter drawer: a grey Reset and the
                primary Search (with the filter-search glyph), right-aligned. */}
            <FilterFooter $sidePad={sidePad}>
              <Button className="pi-12" style={{ '--btn-bg': '#9d9d9d' }} onClick={resetDraft}>
                {t('Reset')}
              </Button>
              <Button
                onClick={applyFilters}
                icon="LmsFilterSearchIcon"
                type="primary"
                size="default"
                style={{ height: 40, boxShadow: 'none', color: '#FFFFFF', fontWeight: 700, fontSize: '12px' }}
              >
                {t('Search')}
              </Button>
            </FilterFooter>
          </FilterSheet>
        </DrawerModal>
      )}
    </>
  );
};

export default AdLicensesListModal;
